/**
 * Hardcoded Astra-drafted questions for the Phase 1 Review & Publish shell.
 *
 * TEMPORARY: this stands in for the Astra agent response. Phase 2 replaces it
 * with a mock agent service (realistic delay + loading state), and Phase 3 with
 * the real agent. Content mirrors the UX prototype's three sample questions.
 *
 * Built against the Helios `QuestionContent` / `SubQuestion` contract (via
 * reviewTypes' re-exports) so it maps 1:1 to the publish request in Phase 3.
 *
 * Do NOT wire real data through this file — it exists only so the review screen
 * renders something during Phase 1.
 */
import { ReviewQuestionStatus, type ReviewQuestion } from './reviewTypes';
import type { SubQuestion } from 'src/types/helios/Question';

const DEFAULT_BUSINESS_UNIT = 'AMZL';

/** Pass/Fail-only sub-question requirement toggles, all off by default. */
const PASS_FAIL_TOGGLES = {
  requireCommentOnFail: false,
  requireEvidenceOnFail: false,
  requireCommentOnPass: false,
  requireEvidenceOnPass: false,
} as const;

/**
 * Default question-level configuration for a fresh Astra draft. `sourcePolicyId`
 * defaults to the SOP the generate screen was given (threaded through from the
 * prompt step), or `''` when none — the field stays editable either way.
 */
function createDefaultConfiguration(
  overview: string,
  sourcePolicyId: string,
): ReviewQuestion['configuration'] {
  return {
    overview,
    businessUnit: DEFAULT_BUSINESS_UNIT,
    divisionName: '',
    categories: [],
    auditScore: 5,
    tagsIncluded: {},
    locationsExcluded: [],
    sourcePolicyId,
    requireCommentOnFail: false,
    requireEvidenceOnFail: false,
    requireCommentOnPass: false,
    requireEvidenceOnPass: false,
    docLinks: [],
  };
}

/** The Astra-drafted audit-criteria fields, minus the title. */
type MockCriteria = Omit<ReviewQuestion['content']['questionDetails'], 'title'>;

/**
 * Assembles a NEEDS_REVIEW question from a single `title` (written once and
 * reused for the panel title, the question detail title, and the config
 * overview), its criteria, and optional sub-questions.
 */
function buildMockQuestion(
  id: string,
  title: string,
  criteria: MockCriteria,
  sourcePolicyId: string,
  subQuestions: SubQuestion[] = [],
): ReviewQuestion {
  return {
    id,
    status: ReviewQuestionStatus.NEEDS_REVIEW,
    title,
    content: {
      questionDetails: { title, ...criteria },
      subQuestions,
    },
    configuration: createDefaultConfiguration(title, sourcePolicyId),
  };
}

/**
 * Factory so each caller/render gets an independent, mutable working copy.
 *
 * @param sourcePolicyId Default Standard Operating Procedure for every drafted
 *   question — the SOP link entered on the generate screen, threaded through so
 *   the config field is prefilled. Defaults to `''` (direct navigation, no SOP).
 */
export function createMockAstraQuestions(sourcePolicyId = ''): ReviewQuestion[] {
  const q1SubQuestions: SubQuestion[] = [
    {
      title: 'Are scanners and label printers functional at every station?',
      orderIndex: 1,
      instruction: 'Test one scan and one label print per station.',
      ...PASS_FAIL_TOGGLES,
    },
    {
      title: 'Is packaging material stocked to par levels?',
      orderIndex: 2,
      instruction:
        'Confirm each station has packaging materials stocked to the defined par level.',
      ...PASS_FAIL_TOGGLES,
    },
  ];

  return [
    buildMockQuestion(
      'astra-q-1',
      'Induct Materials Readiness Before Start of Shift',
      {
        questionText:
          'Are all induct stations stocked with required materials and staged correctly before the start of shift?',
        auditInstruction:
          'Walk each induct station 15 minutes before shift start. Confirm scanners, label printers, and packaging materials are present and functional. Verify staging matches the loading SOP.',
        passCriteria:
          'All induct stations have functional scanners and printers, required packaging materials on hand, and staging matches the SOP with no missing items.',
        failCriteria:
          'Any induct station is missing required materials, has a non-functional scanner or printer, or staging does not match the SOP.',
        justification:
          'Readiness at start of shift directly impacts throughput and defect rates during the induct process.',
      },
      sourcePolicyId,
      q1SubQuestions,
    ),
    buildMockQuestion(
      'astra-q-2',
      'Line Loader Package Loading Methods',
      {
        questionText:
          'Are line loaders following the correct package loading methods for the route type?',
        auditInstruction:
          'Observe line loaders during loading. Confirm packages are loaded per the loading SOP for the route type and that load quality standards are met.',
        passCriteria:
          'Packages are loaded according to the SOP for the route type with load quality standards met.',
        failCriteria:
          'Packages are loaded out of sequence, damaged during loading, or the method does not match the SOP.',
        justification:
          'Correct loading methods reduce damage and improve delivery efficiency downstream.',
      },
      sourcePolicyId,
    ),
    buildMockQuestion(
      'astra-q-3',
      'Trash and Debris Removal in Induct Area',
      {
        questionText: 'Is the induct area free of trash and debris throughout the shift?',
        auditInstruction:
          'Inspect the induct area aisles and workstations. Confirm they are free of trash, debris, and trip hazards.',
        passCriteria:
          'Induct area aisles and workstations are free of trash, debris, and trip hazards.',
        failCriteria:
          'Trash, debris, or trip hazards are present in the induct area aisles or workstations.',
        justification:
          'A clean induct area is a safety and quality requirement and reduces the risk of incidents.',
      },
      sourcePolicyId,
    ),
    buildMockQuestion(
      'astra-q-4',
      'Personal Protective Equipment Compliance',
      {
        questionText:
          'Are associates in the induct area wearing the required personal protective equipment?',
        auditInstruction:
          'Observe associates across the induct area. Confirm safety vests, footwear, and any zone-specific PPE match the site safety SOP.',
        passCriteria:
          'All observed associates wear the required PPE for the induct zone per the safety SOP.',
        failCriteria:
          'Any associate is missing required PPE or is wearing non-compliant equipment.',
        justification:
          'PPE compliance is a core safety control and directly reduces recordable incidents.',
      },
      sourcePolicyId,
    ),
    buildMockQuestion(
      'astra-q-5',
      'Conveyor Emergency Stop Accessibility',
      {
        questionText:
          'Are conveyor emergency stop controls unobstructed and functional along the induct line?',
        auditInstruction:
          'Walk the induct conveyor line. Confirm each e-stop is visible, unobstructed, labeled, and passes a functional pull/press test.',
        passCriteria:
          'Every e-stop along the line is accessible, clearly labeled, and functional on test.',
        failCriteria:
          'Any e-stop is blocked, unlabeled, damaged, or fails the functional test.',
        justification:
          'Accessible emergency stops are a critical safety requirement for powered conveyor equipment.',
      },
      sourcePolicyId,
    ),
  ];
}
